import React, { useState } from 'react';
import { Alert, Button, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import uuid from 'react-uuid';
import { SchedulerAreaData } from '../../api';
import { maskToIndexes } from '../../utils/maskUtils';
import { AreaRange, maskToRange, overlappingAreaIds, rangeToMask, validateRange } from './maskRanges';

type AreaRangesEditorProps = {
  areas: SchedulerAreaData[];
  resourceCount: number;
  languages: string[];
  disabled: boolean;
  onChange: (areas: SchedulerAreaData[]) => void;
  /** Raised while any range is unusable, so the form can refuse to save. */
  onValidityChange: (isValid: boolean) => void;
};

type SortableAreaRowProps = {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
};

const SortableAreaRow = ({ id, disabled, children }: SortableAreaRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...attributes}>
      <Grid container alignItems="flex-start" wrap="nowrap" gap={1}>
        <IconButton
          size="small"
          disabled={disabled}
          {...listeners}
          sx={{ cursor: disabled ? 'not-allowed' : 'grab', '&:active': { cursor: 'grabbing' }, touchAction: 'none' }}>
          <DragIndicator />
        </IconButton>
        <Grid flexGrow={1}>{children}</Grid>
      </Grid>
    </div>
  );
};

type DraftKey = string;
type Draft = { fromIndex: number; toIndex: number };

/**
 * Areas are authored as named ranges of resources and stored as masks. A mask that is not one
 * contiguous run cannot be shown as a range, so it is displayed read-only rather than being
 * rewritten into something the admin did not ask for.
 *
 * Typed values are held as drafts and only written to the mask once they are valid. Quietly
 * clamping or flipping a bad range would save a different area than the one on screen.
 */
export const AreaRangesEditor = ({
  areas,
  resourceCount,
  languages,
  disabled,
  onChange,
  onValidityChange,
}: AreaRangesEditorProps) => {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<Record<DraftKey, Draft>>({});
  const overlapping = overlappingAreaIds(areas);

  const updateArea = (index: number, patch: Partial<SchedulerAreaData>) =>
    onChange(areas.map((area, position) => (position === index ? { ...area, ...patch } : area)));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      const oldIndex = areas.findIndex((area) => area.id === active.id);
      const newIndex = areas.findIndex((area) => area.id === over.id);
      onChange(arrayMove(areas, oldIndex, newIndex));
    }
  };

  const rangeOf = (area: SchedulerAreaData): AreaRange | null => drafts[area.id] ?? maskToRange(area.mask);

  const setBound = (area: SchedulerAreaData, index: number, current: AreaRange, patch: Partial<AreaRange>) => {
    const next = { ...current, ...patch };
    const guard = validateRange(next, resourceCount);
    const remainingDrafts = { ...drafts };

    if (guard) {
      // Keep what was typed on screen; the stored mask stays at its last usable value.
      remainingDrafts[area.id] = next;
    } else {
      delete remainingDrafts[area.id];
      updateArea(index, { mask: rangeToMask(next, resourceCount) });
    }

    setDrafts(remainingDrafts);
    onValidityChange(Object.keys(remainingDrafts).length === 0);
  };

  const boundField = (
    area: SchedulerAreaData,
    index: number,
    range: AreaRange,
    bound: 'fromIndex' | 'toIndex',
    guard: ReturnType<typeof validateRange>,
  ) => (
    <TextField
      size="small"
      type="number"
      sx={{ width: 100 }}
      label={t(`sharedResources.schedulerConfigs.${bound === 'fromIndex' ? 'fromResource' : 'toResource'}`)}
      value={range[bound] + 1}
      disabled={disabled}
      error={Boolean(guard)}
      inputProps={{ min: 1, max: resourceCount, dir: 'ltr' }}
      onChange={(event) => setBound(area, index, range, { [bound]: Number(event.target.value) - 1 })}
    />
  );

  return (
    <Grid container flexDirection="column" wrap="nowrap" gap={1}>
      <Typography variant="subtitle2">{t('sharedResources.schedulerConfigs.areas')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('sharedResources.schedulerConfigs.areasHelp', { total: resourceCount })}
      </Typography>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={areas.map((area) => area.id)} strategy={verticalListSortingStrategy}>
          {areas.map((area, index) => {
            const range = rangeOf(area);
            const guard = range ? validateRange(range, resourceCount) : null;

            return (
              <SortableAreaRow key={area.id} id={area.id} disabled={disabled}>
                <Grid container flexDirection="column" wrap="nowrap">
                  <Grid container alignItems="center" gap={1} wrap="nowrap">
                    {languages.map((lang) => (
                      <TextField
                        key={lang}
                        size="small"
                        sx={{ width: 140 }}
                        label={t(`emailTemplates.${lang}`)}
                        value={(area.label as Record<string, string | undefined>)?.[lang] ?? ''}
                        disabled={disabled}
                        onChange={(event) =>
                          updateArea(index, { label: { ...area.label, [lang]: event.target.value } })
                        }
                      />
                    ))}

                    {range ? (
                      <>
                        {boundField(area, index, range, 'fromIndex', guard)}
                        {boundField(area, index, range, 'toIndex', guard)}
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {t('sharedResources.schedulerConfigs.rangeSize', {
                            count: guard ? 0 : range.toIndex - range.fromIndex + 1,
                          })}
                        </Typography>
                      </>
                    ) : (
                      <Tooltip title={t('sharedResources.schedulerConfigs.customMaskHelp')}>
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>
                          {t('sharedResources.schedulerConfigs.customMask', {
                            resources: maskToIndexes(area.mask)
                              .map((resource) => resource + 1)
                              .join(', '),
                          })}
                        </Typography>
                      </Tooltip>
                    )}

                    <IconButton
                      disabled={disabled}
                      onClick={() => {
                        const remaining = { ...drafts };
                        delete remaining[area.id];
                        setDrafts(remaining);
                        onValidityChange(Object.keys(remaining).length === 0);
                        onChange(areas.filter((unused, i) => i !== index));
                      }}>
                      <Delete />
                    </IconButton>
                  </Grid>

                  {guard && (
                    <Typography variant="caption" color="error">
                      {t(`sharedResources.schedulerConfigs.range_${guard}`, { max: resourceCount })}
                    </Typography>
                  )}
                </Grid>
              </SortableAreaRow>
            );
          })}
        </SortableContext>
      </DndContext>

      {overlapping.length > 0 && (
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          {t('sharedResources.schedulerConfigs.areasOverlapWarning')}
        </Alert>
      )}

      <Grid>
        <Button
          size="small"
          startIcon={<Add />}
          disabled={disabled || resourceCount < 1}
          onClick={() =>
            onChange([
              ...areas,
              { id: uuid(), label: { he: '' }, mask: rangeToMask({ fromIndex: 0, toIndex: 0 }, resourceCount) },
            ])
          }>
          {t('sharedResources.schedulerConfigs.addArea')}
        </Button>
      </Grid>
    </Grid>
  );
};
